import useFetchDataWithOrg from 'hooks/useFetchDataWithOrg';
import { useMemo, useRef } from 'react';
import useZoomByMouseWheel from '../Hooks/useZoomByMouseWheel';
import { createForegroundPositionExpression, createHandleFgImgMouseMove } from '../IGconfig';
import CompetitionLogo from '../Parts/CompetitionLogo';
import DotRotatedText from '../Parts/DotRotatedText';
import IGHeader from '../Parts/IGHeader';
import TablePlayers from '../Parts/TablePlayers';

const IG11_PointLeaders_Core = ({ 
  exportRef,
  metadata,
  size,
  fgImgPositionX,
  fgImgPositionY,
  setFgImgPositionX,
  setFgImgPositionY,
  fgImgZoom,
  setFgImgZoom,
  fgPictureUrl,
  fgImgMargin,
  backgroundMode,
  backgroundImgUrl,
  backgroundColor,
  phaseIdsAll,
  selectedTeams = [],
  topCount,
  enablePositioning = true,
  useImageProxy = true
}) => {
  const fgImgRef = useRef(null);
  const effectiveMode = backgroundMode ?? (backgroundImgUrl ? 'IMAGE' : backgroundColor ? 'COLOR' : 'DEFAULT');
  
  // Always call hooks, but conditionally use positioning functionality
  useZoomByMouseWheel(fgImgRef, enablePositioning ? fgImgZoom : null, enablePositioning ? setFgImgZoom : () => {});
  const handleFgImgMouseMove = enablePositioning 
    ? createHandleFgImgMouseMove(setFgImgPositionX, setFgImgPositionY)
    : null;

  // Load player stats data using public API
  const playerStats = useFetchDataWithOrg(phaseIdsAll != null
    ? `/public/playersStats?phaseIds=${phaseIdsAll.length > 0 ? phaseIdsAll.join(',') : ''}`
    : null);

  const data = useMemo(() => {
    const raw = playerStats?.data?.map((player) => ({
      FirstName: player.FirstName,
      LastName: player.LastName,
      PlayerLogo: player.PlayerLogo?.replace("[size]", "cropped_md"),
      PlayerLevelCode: player.PlayerLevelCode,
      TeamLogo: player.TeamLogo?.replace("[size]", "cropped_md"),
      TeamId: player.TeamId,
      Z: player.GP,
      G: player.G,
      A: player.A,
      P: player.P,
      TM: player.PIM,
      Stars: player.Stars,
    })) ?? [];

    // When teams are selected: strict filter – only players from selected teams
    const filtered = selectedTeams.length > 0
      ? raw.filter((player) => selectedTeams.includes(player.TeamId))
      : raw;

    const sorted = [...filtered].sort((a, b) => {
      if (a.P !== b.P) {
        return b.P - a.P;
      }
      if (a.G !== b.G) {
        return b.G - a.G;
      }
      return a.GP - b.GP;
    });

    // When topCount is set: show only top N; when empty/undefined: no limit
    const sliced = (topCount != null && topCount > 0)
      ? sorted.slice(0, Math.min(100, topCount))
      : sorted;

    return sliced.map((player, index) => ({
      ...player,
      rank: index + 1
    }));
  }, [playerStats, selectedTeams, topCount]);

  if (phaseIdsAll == null) {
    return null;
  }

  return (
    <article
      ref={exportRef}
      className="mx-auto w-[625px] bg-hockey-page-bg font-header text-white"
      style={{
        width: `${size?.width}px`,
        height: `${size?.height}px`,
        paddingTop: `${fgImgMargin.top}px`,
        paddingLeft: `${fgImgMargin.left}px`,
        paddingRight: `${fgImgMargin.right}px`,
        paddingBottom: `${fgImgMargin.bottom}px`,
        fontSize: '14px', // this is important - in design mode it is inherited from somewhere, but in EmbedInfographic it is not, we need to have this on par
        boxSizing: 'border-box',
        ...(effectiveMode === 'COLOR' && backgroundColor
          ? { backgroundColor }
          : effectiveMode === 'IMAGE' && backgroundImgUrl
            ? {
                backgroundImage: `url('${useImageProxy ? `${process.env.REACT_APP_IMAGE_PROXY_URL}?url=${backgroundImgUrl}` : backgroundImgUrl}')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }
            : {
                backgroundColor: '#323f50',
              }),
      }}
    >
      <div className="flex h-full flex-col items-center justify-between">

        <div className="p-8 pb-6">
          <IGHeader
            title={`League leaders`}
            league={metadata.GroupName}
            leagueStyle={{ color: metadata.GroupColor ?? '#777777' }}
            small
            date={metadata.Date}
          />
          <TablePlayers data={data} highlightColID="p" />
        </div>

        <div className='w-full'>
          <DotRotatedText firstText="Points" secondText="Leaders" />

          <div
            ref={fgImgRef}
            onMouseMove={handleFgImgMouseMove}
            className='w-full z-20 relative aspect-[24/9]'
            style={{
              backgroundImage: `url('${useImageProxy ? `${process.env.REACT_APP_IMAGE_PROXY_URL}?url=${fgPictureUrl}` : fgPictureUrl}')`,
              backgroundSize: `${fgImgZoom ?? 100}%`,
              backgroundPosition: createForegroundPositionExpression(fgImgPositionX, fgImgPositionY),
              backgroundRepeat: 'no-repeat',
              cursor: enablePositioning ? 'grab' : 'default',
              clipPath: "polygon(450px 0, 100% 0, 100% 100%, 0 100%, 0 70px)",
            }}
            onMouseDown={enablePositioning ? (e) => e.target.style.cursor = 'grabbing' : undefined}
            onMouseUp={enablePositioning ? (e) => e.target.style.cursor = 'grab' : undefined}
          ></div>
          <CompetitionLogo
            className="mx-auto scale-75 py-2"
            src={metadata.CompetitionLogo}
            title={metadata.CompetitionName}
            season={metadata.SeasonName}
            useImageProxy={useImageProxy}
          />
        </div>
      </div>
    </article>
  );
};

export default IG11_PointLeaders_Core;
